// 通用 useQuery 包装 — 把项目里 30+ 处重复模板压成一行调用
//
// 之前每个 query 都要写:
//   useQuery({
//     queryKey: ['xxx', pid],
//     queryFn: () => axios.get('/api/...', { params: { project_id: pid } }).then(r => r.data),
//     enabled: Number(pid) > 0,
//     staleTime: STALE_TIME.MEDIUM,
//     refetchOnWindowFocus: false,
//   })
//
// 用 useApiQuery 后:
//   const { data } = useApiQuery(['dashboard-tasks', pid], `/api/schedule/${pid}/tasks`, { page_size: 100 })
//
// 任何 useQuery option 都可经 extra 覆盖;默认 staleTime 用 STALE_TIME.MEDIUM(60s),
// 因为绝大多数调用方都写了 60_000。
//
// 2026-08-03 R-Audit:加 extra.module 选项。
// 传入后 query 自动检查用户对指定模块的权限(查 usePermissions().canViewModule),
// 无权限时 query disabled,不发请求,避免后端 403 刷屏。
// 后端很多端点(safety-quality/issues、risk/stats/summary 等)走 verify_module_access(..., "xxx", ...),
// 前端如果直接调而不带模块鉴权,无权限用户会刷出一堆 403。
// R-2026-08-22:页面「安全质量」canonical 是 'safety_quality',别再用 'safety'/'quality' 字面量。
// 模式:useApiQuery(key, url, params, { module: 'safety_quality' })
// 显式 options.enabled=true/false 可覆盖默认行为。

import { useQuery, keepPreviousData } from '@tanstack/react-query'
import axios from 'axios'
import { STALE_TIME } from '../constants/staleTime.js'
import usePermissions from './usePermissions'

// R-2026-09-22 (数据隔离空态): 无可见项目账号的合法路由是 /{module}/0,
// 此时任何项目数据请求都会被后端 404(项目不存在),console 刷屏。
// URL 路径段恰为 '0'(如 /api/schedule/0/baselines、/api/investment/projects/0/phases)
// 或 params.project_id === 0 的 GET 一律 disabled — 项目 id 自增从 1 起,
// 0 只可能是「未选项目」哨兵。此守卫不可通过 extra.enabled 覆盖。
function _isZeroProjectRequest(url, params) {
  if (params && (params.project_id === 0 || params.project_id === '0')) return true
  if (typeof url !== 'string') return false
  return url.split('?')[0].split('/').includes('0')
}

export function useApiQuery(queryKey, url, params, extra = {}) {
  const { canViewModule } = usePermissions()
  // 模块鉴权默认:传 extra.module 时,无权限 → disabled
  const moduleOk = extra.module ? canViewModule(extra.module) : true
  // enabled 优先级:显式 true/false 永远胜出;未传时,模块鉴权(默认 true)∩其他条件
  const baseEnabled = extra.enabled !== undefined ? extra.enabled : moduleOk
  // R-2026-09-22: pid=0 哨兵拦截(含显式 enabled=true 的调用,如 Schedule baselines 的 !!projectId 误判)
  const zeroProject = _isZeroProjectRequest(url, params)
  const baseEnabledWithGuard = baseEnabled && !zeroProject
  // 抽离 extra 中我们已处理过的字段,避免和 ...extra 冲突
  const { module: _moduleIgnored, enabled: _enabledIgnored, ...rest } = extra
  // R-fix-2026-08-17(F-3): 默认 throwOnError:false,useApiQuery 失败时不抛,
  // 让业务页面用 isError 解构出来显示 <LoadError/>(之前 16+ 页面无 isError
  // 处理 → 后端 5xx 时静默返回空数据,用户误以为'无数据')。
  // 显式 throwOnError:true 可覆盖(少数 mutation 仍要 throw)
  const throwOnError = rest.throwOnError ?? false
  const { throwOnError: _ignored, ...restClean } = rest
  // R-fix-2026-08-24 (fail2ban 根因 B): 默认 401 不重试 — token 失效由 useAuth 拦截器统一处理,
  // 这里继续轮询只会再触发 401 计数被 nginx-auth jail 误锁真实用户 IP。
  // R-audit-2026-09-24 phase-H1 P0: 加 429 不重试 — 后端限流 60/min,
  // 第二次必再 429,白白消耗配额。429 由 React Query 暴露给 UI 让用户感知或触发退避策略。
  // 显式 retry:true/false/number 可覆盖。
  const defaultRetry = (failureCount, error) => {
    const status = error?.response?.status
    if (status === 401 || status === 429) return false
    return failureCount < 1
  }
  return useQuery({
    queryKey,
    // R-fix-2026-09-06: 权限拒绝通道归一 — 无模块权限时后端按既定设计返回
    // 200 + {items, total, _permission_denied}(R-fix-2026-08-22, 避免 403 刷屏),
    // 但页面普遍按 `const { data: x = [] } = useApiQuery(...)` 消费, 对象会令
    // x.map 整页崩(成员账户 Schedule 白屏根因)。此处统一归一为 items 数组;
    // 需要读取 _permission_denied 详情的场景走 useAuth 独立机制(ProjectPermissionBanner)。
    queryFn: () => axios.get(url, { params }).then(r => {
      const d = r.data
      if (d && typeof d === 'object' && !Array.isArray(d) && '_permission_denied' in d) {
        return Array.isArray(d.items) ? d.items : []
      }
      return d
    }),
    enabled: baseEnabledWithGuard,
    staleTime: restClean.staleTime ?? STALE_TIME.MEDIUM,
    refetchOnWindowFocus: restClean.refetchOnWindowFocus ?? false,
    throwOnError,
    retry: restClean.retry ?? defaultRetry,
    // R-2026-08-31: 切项目 / 切参数时显示上一个数据,避免空白闪烁
    placeholderData: restClean.placeholderData ?? keepPreviousData,
    ...restClean,
  })
}
